import React from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../i18n/LanguageContext';

const Location = () => {
  const { t } = useLanguage();

  const addressLines = ['79 Rue Saint-Antoine', 'Sainte-Geneviève, QC', 'H9H 2P6'];
  const mapsQuery = 'The Lash Room - Studio de Beauté, 79 Rue Saint-Antoine, Montréal, QC H9H 2P6';
  const googleMapsUrl =
    'https://www.google.com/maps/place/The+Lash+Room+-+Studio+de+Beaut%C3%A9/@45.4860965,-73.8680522,17z/data=!4m15!1m8!3m7!1s0x4cc93b08ba2eb2af:0x7a8b91866aaaac73!2s79+Rue+Saint-Antoine,+Montr%C3%A9al,+QC+H9H+2P6!3b1!8m2!3d45.4860965!4d-73.8680522!16s%2Fg%2F11x11hrjkp!3m5!1s0x4cc93bf87f2a25c7:0x128b4abe3968f0ac!8m2!3d45.4860965!4d-73.8680522!16s%2Fg%2F11x900mjc3?entry=ttu&g_ep=EgoyMDI2MDQxNS4wIKXMDSoASAFQAw%3D%3D';

  return (
    <section id="location" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-brand-sand rounded-3xl overflow-hidden flex flex-col lg:flex-row">
          
          {/* Info Side */}
          <div className="p-10 md:p-16 lg:w-1/2 flex flex-col justify-center">
            <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-8">{t('location.title')}</h2>
            
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="text-brand-taupe mt-1">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-medium text-brand-dark mb-1">{t('location.addressLabel')}</h4>
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-muted font-light hover:text-brand-taupe transition-colors"
                    aria-label={t('location.openAddressLabel')}
                  >
                    {addressLines[0]}<br />
                    {addressLines[1]}<br />
                    {addressLines[2]}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="text-brand-taupe mt-1">
                  <SafeIcon icon={FiPhone} className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-medium text-brand-dark mb-1">{t('location.phoneLabel')}</h4>
                  <a href="tel:5142954267" className="text-brand-muted font-light hover:text-brand-taupe transition-colors">
                    (514) 295-4267
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="text-brand-taupe mt-1">
                  <SafeIcon icon={FiClock} className="w-6 h-6" />
                </div>
                <div className="w-full max-w-xs">
                  <h4 className="font-medium text-brand-dark mb-3">{t('location.hoursLabel')}</h4>
                  <ul className="space-y-2 text-brand-muted font-light text-sm">
                    <li className="flex justify-between"><span>{t('location.days.wedThu')}</span> <span>9:00 AM – 7:00 PM</span></li>
                    <li className="flex justify-between"><span>{t('location.days.fri')}</span> <span>9:00 AM – 5:00 PM</span></li>
                    <li className="flex justify-between"><span>{t('location.days.sat')}</span> <span>10:00 AM – 3:00 PM</span></li>
                    <li className="flex justify-between text-brand-taupe"><span>{t('location.days.sunTue')}</span> <span>{t('location.days.closed')}</span></li>
                  </ul>
                  <p className="mt-4 text-sm font-medium text-brand-dark italic">{t('location.byAppointmentOnly')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Map Side */}
          <div className="lg:w-1/2 min-h-[400px] relative bg-brand-cream inset-0">
            <iframe
              title="Google Map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`}
              className="w-full h-full min-h-[400px] border-0 pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0"
              aria-label={t('location.openMapLabel')}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white p-4 rounded-full shadow-lg text-brand-taupe animate-bounce">
                <SafeIcon icon={FiMapPin} className="w-8 h-8" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Location;
